import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { NavItem } from './useProjectNavigation'

export interface StackModeSelectionParams {
  /** Whether the deck (stack mode) is currently rendered instead of the board. */
  isStackMode: boolean
  focusedNavIndex: number | null
  flatNavList: readonly NavItem[]
  selectedIds: ReadonlySet<string>
  /** Single-select setter from useProjectNavigation. */
  selectTask: (taskId: string) => void
}

export interface StackModeSelection {
  /** Task the deck should open on — `null` falls back to the first card. */
  selectedTaskId: string | null
  /** Deck callback: the card currently in focus (scroll, arrows, advance). */
  onActiveTaskChange: (taskId: string | null) => void
}

/**
 * Carries the task selection across a window-mode switch (list ↔ stack).
 *
 * Both views track "the current task" in their own way — the board via
 * `useProjectNavigation` (`selectedIds` + `focusedNavIndex`), the deck via its
 * internal focus card — and the deck is mounted fresh on every switch. Without
 * this bridge, entering stack mode always lands on card 1 and returning leaves
 * the board on whatever was selected before.
 *
 * Direction in (list → stack): `selectedTaskId` names the task the deck opens
 * on. The focused row wins; with a multi-selection and no task focused, the
 * first selected task in list order is used.
 *
 * Direction out (stack → list): the deck reports its focus card continuously,
 * but that lands in a **ref** — it changes on every card dealt and would
 * re-render ProjectPage on each scroll tick. The selection is only written back
 * on the `stack → not stack` edge, in one `selectTask()` call.
 */
export function useStackModeSelection({
  isStackMode,
  focusedNavIndex,
  flatNavList,
  selectedIds,
  selectTask,
}: StackModeSelectionParams): StackModeSelection {
  const selectedTaskId = useMemo<string | null>(() => {
    const focused = focusedNavIndex !== null ? flatNavList[focusedNavIndex] : null
    if (focused?.type === 'task') return focused.id
    // No focused row (or a section is focused) — fall back to the topmost
    // selected task so a multi-selection still has a defined entry point.
    for (const item of flatNavList) {
      if (item.type === 'task' && selectedIds.has(item.id)) return item.id
    }
    return null
  }, [focusedNavIndex, flatNavList, selectedIds])

  const deckActiveIdRef = useRef<string | null>(null)
  const onActiveTaskChange = useCallback((taskId: string | null) => {
    deckActiveIdRef.current = taskId
  }, [])

  // Ref-mirror the setter so the edge effect below only depends on the mode.
  const selectTaskRef = useRef(selectTask)
  selectTaskRef.current = selectTask

  const wasStackModeRef = useRef(isStackMode)
  useEffect(() => {
    const wasStackMode = wasStackModeRef.current
    wasStackModeRef.current = isStackMode
    if (!wasStackMode || isStackMode) return

    const taskId = deckActiveIdRef.current
    deckActiveIdRef.current = null
    if (!taskId) return

    selectTaskRef.current(taskId)
    // The board has just re-mounted; wait a frame so the row exists before
    // scrolling it into view (same deferral as ProjectPage's task-jump).
    const rafId = requestAnimationFrame(() => {
      document
        .querySelector(`[data-task-id="${CSS.escape(taskId)}"]`)
        ?.scrollIntoView({ block: 'nearest' })
    })
    return () => cancelAnimationFrame(rafId)
  }, [isStackMode])

  return { selectedTaskId, onActiveTaskChange }
}
